import React from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Grid from "@mui/material/Grid2";
import { Circle } from "@phosphor-icons/react";
import { statusColors } from "../../utilities";
import trackOrderIcon from "../../assets/svg/track-order-icon.svg";
import { useNavigate } from "react-router";

const OrderItem = ({ item }) => {
  const orderedProduct = item?.ordered_products?.[0];
  const parentDetails = orderedProduct?.parent_product_details;
  const childDetails = orderedProduct?.product_details;

  const imageUrl =
    parentDetails?.length > 0
      ? parentDetails[0]?.images?.[0]?.app_product_image
      : childDetails?.[0]?.images?.[0]?.app_product_image;

  const productName =
    parentDetails?.length > 0
      ? parentDetails[0]?.product_name
      : childDetails?.[0]?.product_name;

  const unit = `${item?.ordered_products[0]?.variation?.weight?.[0]?.weight_name} ${item?.ordered_products[0]?.variation?.weight?.[0]?.unit?.unit_name}`;
  const price = item?.order_overall_totall;
  const orderId = item?.order_id;
  const deliveryDate = item?.delivery_date;
  const calculateTotalQty = (item) => {
    const products = item?.ordered_products || [];

    const comboParentIds = new Set();
    let normalQty = 0;

    products.forEach((p) => {
      const parentId = p?.parent_product_details?.[0]?.id;

      if (parentId) {
        comboParentIds.add(String(parentId));
      } else {
        normalQty += Number(p?.product_quantity) || 1;
      }
    });

    const comboQty = comboParentIds.size;

    const addonIds = new Set();
    (item?.ordered_addons || []).forEach((a) => {
      addonIds.add(String(a?.order_addons_id));
    });

    return comboQty + normalQty + addonIds.size;
  };
  const navigate = useNavigate();

  const handleTrackOrderClick = () => {
    navigate(`/account/my-orders/${orderId}`);
  };

  const totalQty = calculateTotalQty(item);
  const isCombo = parentDetails?.length > 0;
  const productCount = item?.ordered_products?.length || 0;
  const showUnit = !isCombo && productCount === 1;
  const statusStyle = statusColors(item) || {};

  return (
    <Grid key={orderId} size={12}>
      <OrderCardShell>
        <Grid
          container
          sx={{
            width: "100%",
            rowGap: 2,
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          <Grid
            size={{ xs: 12, sm: 7, md: 5 }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={productName}
              sx={{
                height: { xs: "72px", md: "84px" },
                width: { xs: "72px", md: "84px" },
                objectFit: "cover",
                borderRadius: "14px",
                border: "1px solid rgba(183, 124, 10, 0.16)",
                background: "rgba(255, 247, 232, 0.62)",
              }}
            />
            <Stack direction="column" sx={{ gap: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "15px", sm: "16px", md: "17px" },
                  lineHeight: "150%",
                  textAlign: "left",
                  color: "#2C2418",
                }}
              >
                {productName}
              </Typography>
              {isCombo && (
                <Chip
                  label="COMBO"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(210, 74, 38, 0.12)",
                    color: "#9B3119",
                    fontWeight: 500,
                    fontSize: { xs: "10px", sm: "12px" },
                    width: "fit-content",
                    borderRadius: "999px",
                    border: "1px solid rgba(210, 74, 38, 0.18)",
                    userSelect: "none",
                  }}
                />
              )}
              {showUnit && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "11px", sm: "12px" },
                    lineHeight: "167%",
                    textAlign: "left",
                    color: "var(--neutral-600)",
                  }}
                >
                  {unit}
                </Typography>
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "11px", sm: "12px" },
                  lineHeight: "167%",
                  textAlign: "left",
                  color: "var(--neutral-600)",
                }}
              >
                Qty: {totalQty}
              </Typography>
              {/* <Typography>Qty: {totalQty}</Typography> */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "13px", sm: "14px" },
                  lineHeight: "157%",
                  textAlign: "left",
                  color: "var(--neutral-black)",
                }}
              >
                {`₹ ${price}`}
              </Typography>
            </Stack>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 5, md: 3 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <MetaPill>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: "#A86E18" }} />
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#4A3A1C" }}>
                #{orderId}
              </Typography>
            </MetaPill>
            <MetaPill>
              <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "#A86E18" }} />
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6E624B" }}>
                {deliveryDate || "Delivery date pending"}
              </Typography>
            </MetaPill>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 12, md: 4 }}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Stack direction="column" sx={{ gap: 1.25, width: { xs: "100%", md: "auto" } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  lineHeight: "150%",
                  textAlign: "left",
                  alignItems: "center",
                  display: "flex",
                  gap: 1,
                  color: statusStyle?.color,
                }}
              >
                <Circle size={20} weight="fill" />
                {`${item?.preview_status}`}
              </Typography>
              {item?.preview_status == "Delivered" && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "11px", sm: "12px" },
                    lineHeight: "167%",
                    textAlign: "left",
                    color: "var(--neutral-600)",
                  }}
                >
                  Your order has been delivered
                </Typography>
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "13px", sm: "14px" },
                  lineHeight: "157%",
                  textAlign: "left",
                  color: "var(--primary-red-base)",
                  textDecoration: "underline",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  "&:hover": {
                    fontWeight: 700,
                    transition: "all 0.1s ease",
                  },
                }}
                onClick={handleTrackOrderClick}
              >
                <img
                  src={trackOrderIcon}
                  alt={`# ${orderId}`}
                  style={{ marginRight: "5px", width: "16px" }}
                />
                Track order
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </OrderCardShell>
    </Grid>
  );
};

const OrderCardShell = styled(Box)(({ theme }) => ({
  width: "100%",
  border: "1px solid rgba(183, 124, 10, 0.22)",
  borderRadius: "18px",
  padding: theme.spacing(2.25),
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,250,242,0.96) 100%)",
  boxShadow: "0 10px 24px rgba(173, 132, 61, 0.08)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
  '&:hover': {
    transform: "translateY(-2px)",
    boxShadow: "0 14px 28px rgba(173, 132, 61, 0.12)",
    borderColor: "rgba(183, 124, 10, 0.34)",
  },
}));

const MetaPill = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  width: "fit-content",
  borderRadius: "999px",
  border: "1px solid rgba(188, 128, 15, 0.18)",
  background: "rgba(255, 247, 232, 0.82)",
  padding: "8px 12px",
}));

export default OrderItem;
